import { useApiQuery } from "@/hooks/useApiQuery";
import type {
  CompanyWebAvatarDTO,
  CompanyWebProfileDTO,
  CompanyWebSocialNetwork,
} from "@shaarp/common";

/** The networks whose picture the server reads, in its order. */
const AVATAR_NETWORKS: CompanyWebSocialNetwork[] = ["facebook", "instagram"];

/** Meta signs its picture URLs for days: an hour-old one still loads. */
const AVATAR_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * The picture of the Facebook page, else the Instagram account, the settled
 * profile kept for the company — asked only when `hasLogo` is false, i.e. no
 * website gives logo.dev a domain, and only when the profile lists one of the
 * two accounts: an Instagram picture costs the server a provider call.
 *
 * A failed read is no picture: the initial already stands in, and the server
 * logs the failure.
 */
export function useCompanyWebAvatar(
  profile: CompanyWebProfileDTO | null,
  hasLogo: boolean,
): string | null {
  const key = profile?.status === "ok" ? profile.key : null;
  const hasAccount = !!profile?.keyData.socialLinks.some((link) =>
    AVATAR_NETWORKS.includes(link.network),
  );
  const { data } = useApiQuery<CompanyWebAvatarDTO>({
    key: ["company-web-avatar", key],
    path: "/company/preview/avatar",
    params: { key },
    staleTime: AVATAR_STALE_TIME_MS,
    enabled: key !== null && hasAccount && !hasLogo,
  });

  return hasLogo ? null : (data?.url ?? null);
}
